/**
 * De qué anuncio llegó la persona (2026-10-12, pauta de TikTok).
 *
 * El anuncio lleva a tbit.app/es/job-apply, y su botón trae a jobs.tbit.app
 * con el `ttclid` del clic, los utm_* y la cookie `_ttp` del píxel de la
 * landing (`?ttp=`). Como el `?ref` (ver `referralCapture.ts`), eso se perdería
 * en el rodeo por auth.tbit.app: se guarda en `localStorage` ANTES de montar
 * React y se manda a `POST /job-apply/api/ad-attribution` cuando hay sesión.
 * El servicio lo usa para reportarle a TikTok el registro, la prueba y el pago.
 *
 * ── LA ÚLTIMA VISITA GANA ──────────────────────────────────────────────────
 *
 * Al revés que el referido: para la pauta cuenta el último anuncio que trajo a
 * la persona antes de registrarse. Una visita SIN parámetros no borra lo
 * guardado (es la vuelta del login). Vence a los 30 días.
 *
 * ── UNA VEZ POR NAVEGADOR, CUANDO YA HAY MEMBRESÍA ─────────────────────────
 *
 * Se manda aunque no haya nada guardado: la IP y el navegador de esa petición
 * también le sirven a TikTok. La membresía NO nace en el login sino al darle
 * «Empezar» en /onboarding, y hasta entonces el servidor contesta 401: eso NO
 * cierra nada, se reintenta en la navegación siguiente (el layout llama en cada
 * cambio de ruta). Solo un 2xx o un 400 dejan la marca de enviado; el servidor
 * igual escribe una sola vez. Un anuncio NUEVO borra la marca. Silenciosa como
 * `referralSync`: nadie espera esta llamada.
 */
import { apiFetch } from '~/lib/api';

export const AD_ATTRIBUTION_KEY = 'job_apply_ad_attr';
export const AD_ATTRIBUTION_SENT_KEY = 'job_apply_ad_attr_sent';

const PARAMS = ['ttclid', 'ttp', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export interface StoredAdAttribution {
  params: Partial<Record<(typeof PARAMS)[number], string>>;
  landing_url?: string;
  captured_at: string;
}

/** Lee la URL de entrada y guarda lo que traiga del anuncio. */
export function captureAdAttributionFromUrl(
  search: string = window.location.search,
  referrer: string = document.referrer,
  href: string = window.location.href,
): void {
  const qs = new URLSearchParams(search);
  const params: StoredAdAttribution['params'] = {};
  for (const p of PARAMS) {
    const v = qs.get(p)?.trim();
    if (v) params[p] = v.slice(0, 200);
  }
  if (!Object.keys(params).length) return;

  // La landing del anuncio es la página de tbit.app que trajo hasta acá; si se
  // entró directo, esta misma.
  const landing = /^https:\/\/tbit\.app\//.test(referrer) ? referrer : href;
  const entry: StoredAdAttribution = { params, landing_url: landing.slice(0, 500), captured_at: new Date().toISOString() };
  try {
    window.localStorage.setItem(AD_ATTRIBUTION_KEY, JSON.stringify(entry));
    // Un anuncio nuevo vuelve a abrir el envío (el servidor decide si escribe).
    window.localStorage.removeItem(AD_ATTRIBUTION_SENT_KEY);
  } catch {
    // Modo privado: se pierde la atribución, no la visita.
  }
}

/** Lo guardado si está vigente, o null. */
export function getStoredAdAttribution(now: number = Date.now()): StoredAdAttribution | null {
  try {
    const raw = window.localStorage.getItem(AD_ATTRIBUTION_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as StoredAdAttribution | null;
    const at = Date.parse(String(entry?.captured_at ?? ''));
    if (!entry?.params || Number.isNaN(at) || now - at > MAX_AGE_MS) return null;
    return entry;
  } catch {
    return null;
  }
}

function alreadySent(): boolean {
  try {
    return window.localStorage.getItem(AD_ATTRIBUTION_SENT_KEY) === '1';
  } catch {
    return true; // sin storage no hay cómo recordar que se mandó: mejor no insistir
  }
}

function markSent(): void {
  try {
    window.localStorage.setItem(AD_ATTRIBUTION_SENT_KEY, '1');
    window.localStorage.removeItem(AD_ATTRIBUTION_KEY);
  } catch {
    /* nada */
  }
}

let inflight: Promise<void> | null = null;
let settled = false;

/** Sólo para las pruebas. */
export function resetAdAttributionSync(): void {
  inflight = null;
  settled = false;
}

export function syncAdAttribution(): Promise<void> {
  if (settled) return Promise.resolve();
  if (inflight) return inflight;
  if (alreadySent()) {
    settled = true;
    return Promise.resolve();
  }
  const entry = getStoredAdAttribution();
  inflight = (async () => {
    try {
      const res = await apiFetch('/job-apply/api/ad-attribution', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...(entry?.params ?? {}), ...(entry?.landing_url ? { landing_url: entry.landing_url } : {}) }),
      });
      // 401/403: todavía no hay membresía (o la sesión se está renovando); se
      // reintenta en la próxima navegación. 400: el cuerpo no sirve, no insistir.
      if (res.ok || res.status === 400) {
        markSent();
        settled = true;
      }
    } catch {
      // Red caída: la próxima sesión lo intenta de nuevo.
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}
